/* eslint-disable max-len */
import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { DealApplicationRequirement } from 'app/services/deals/deal-application-requirement.model';
import { DealDocumentsService } from 'app/services/deals/deal-documents.service';
import { catchError, map, take, tap } from 'rxjs/operators';
import { 
  AddDocumentCategory,
  AttachDealDocument,
  AttachAdvancedDealDocument,
  ClearDealDocumentRequirements,
  DetachDealDocument,
  GetAdvancedDocumentRequirements,
  GetDealDocumentRequirements,
  UploadDealDocument,
  SetDocumentRequirementsMet,
  GetAdvancedDealDocuments,
  AdvancedDealDocumentPost,
  ReviewDealDocument,
  RejectDealDocument,
  ClearDealDocumentStatus,
  PostDocumentRequirements,
  DeleteDocumentRequest,
  PatchDealDocument
} from './deal-document.actions';
import { DealDocumentUtility } from './deal-document-utility';
import _ from 'lodash';
import { DocumentsService } from 'app/services/documents/documents.service';
import { throwError, of } from 'rxjs';
import { Alert } from 'app/models/dto/alert.model';
import { CreateNewDealAlert } from '../deal-alerts/deal-alerts.actions';
import { Injectable } from '@angular/core';
import { BorrowerValueState } from 'app/store/borrower-value/borrower-value.state';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { DocumentCategory, DocumentRequirements } from 'app/models/document-requirement/document-requirement.model';
import { Document } from 'app/models/dto/document.model';
import { LAST_24_MONTHS_SELECT_OPTIONS } from 'app/documents/document-upload-dialog/document-upload-constants';

export class DealDocumentStateModel {
  uploading: boolean;
  loading: boolean;
  advancedDealDocuments: { 
    dealId: number; 
    documents: Document[];
  }[];
  dealDocumentRequirements: {
    dealId: number;
    documentRequirements: {
      contractRequirements: DealApplicationRequirement[];
      offerRequirements: DealApplicationRequirement[];
      otherDocs: any;
    };
    requirementsMet: boolean;
  }[];
  advDealDocumentRequirements: {
    dealId: number;
    documentRequirements: DocumentRequirements;
  }[];
  dealsLoadingRequirements: Record<number, boolean>;
  dealsLoadingDocuments: Record<number, boolean>;
}

@State<DealDocumentStateModel>({
	name: 'documentRequirements',
  defaults: {
    uploading: false,
    loading: false,
    dealDocumentRequirements: [],
    advDealDocumentRequirements: [],
    advancedDealDocuments: [],
    dealsLoadingRequirements: {},
    dealsLoadingDocuments: {}
	}
})

@Injectable()

export class DealDocumentState {
  @Selector()
  static advancedDealDocuments(state: DealDocumentStateModel) {
    return (dealId: number) => {
      const dealDocuments = state.advancedDealDocuments.find(documents => documents.dealId === dealId);
      return dealDocuments ? dealDocuments.documents : [];
    };
  }

  @Selector()
  static dealDocumentRequirements(state: DealDocumentStateModel) {
    return (dealId: number) => {
      const dealDocumentRequirements = state.dealDocumentRequirements.find(requirements => requirements.dealId === dealId);
      return dealDocumentRequirements ? dealDocumentRequirements.documentRequirements : [];
    };
  }

  @Selector()
  static advancedDealDocumentRequirements(state: DealDocumentStateModel) {
    return (dealId: number) => {
      const dealDocumentRequirements = state.advDealDocumentRequirements.find(requirements => requirements.dealId === dealId);
      return dealDocumentRequirements ? dealDocumentRequirements.documentRequirements : { satisfied: [], remaining: [] };
    };
  }

  @Selector()
  static remainingTimeOptionsByRequiredDocumentCategory(state: DealDocumentStateModel) {
    return (dealId: number, categories: DocumentCategory[]) => {
      const theLast10Years = Array.from({ length: 10 }, (v, i) => {
        const year = new Date().getFullYear() - i;
        return year.toString();
      });

      const { documentRequirements: dealDocumentRequirements } = state.advDealDocumentRequirements.find(requirements => requirements.dealId === dealId) ?? { documentRequirements: { satisfied: [], remaining: [] } };
      const timeClassificationsAlreadyUsed = [ ...dealDocumentRequirements.satisfied, ...dealDocumentRequirements.remaining ]
        .filter(requirement => {
          return !!requirement.timePeriod;
        }).reduce((acc, requirement) => {
          if (acc[requirement.documentCategory.id]) {
            acc[requirement.documentCategory.id].push(requirement.timePeriod);
          } else {
            acc[requirement.documentCategory.id] = [ requirement.timePeriod ];
          }

          return acc;
        }, {});
        return categories
          .filter(category => category.timeClassification)
          .reduce((acc, category) => {
            if (category.timeClassification === 'month') {
              acc[category.id] = timeClassificationsAlreadyUsed[category.id] ? 
              LAST_24_MONTHS_SELECT_OPTIONS.filter(option => !timeClassificationsAlreadyUsed[category.id].includes(option.value)) : 
              LAST_24_MONTHS_SELECT_OPTIONS;
            } else {
              acc[category.id] = timeClassificationsAlreadyUsed[category.id] ? 
              theLast10Years.filter(option => !timeClassificationsAlreadyUsed[category.id].includes(option)) : 
              theLast10Years;
            }

            return acc;
          }, {});
      };
  }

  @Selector()
  static dealsLoadingRequirements(state: DealDocumentStateModel) {
    return state.dealsLoadingRequirements;
  }

  @Selector()
  static dealLoadingRequirements(state: DealDocumentStateModel) {
    return (dealId: number) => state.dealsLoadingRequirements[dealId] || false;
  }

  @Selector()
  static dealLoadingDocuments(state: DealDocumentStateModel) {
    return (dealId: number) => state.dealsLoadingDocuments[dealId] || false;
  }

  @Selector()
  static hasSignedApplication(state: DealDocumentStateModel) {
    return (dealId: number) => {
      const dealDocumentRequirements = state.dealDocumentRequirements.find(requirements => requirements.dealId === dealId);
      const signedApplication = dealDocumentRequirements
        ? dealDocumentRequirements.documentRequirements.offerRequirements.find(req => req.category.value === 'applicationSigned')
        : false;
      return (signedApplication && signedApplication.requirement && signedApplication.requirement.length > 0);
    };
  }

  @Selector()
	static uploading(state: DealDocumentStateModel) {
    return state.uploading;
  }

  @Selector()
  static loading(state: DealDocumentStateModel) {
    return state.loading;
  }

  @Selector()
  static requirementsMet(state: DealDocumentStateModel) {
    return (dealId: number) => {
      const dealDocumentRequirements = state.dealDocumentRequirements.filter(requirements => requirements.dealId === dealId);
      return dealDocumentRequirements.length ? dealDocumentRequirements[0].requirementsMet : false;
    };
  }

  constructor(
    private dealDocumentsService: DealDocumentsService,
    private documentsService: DocumentsService,
    private dealDocumentUtility: DealDocumentUtility,
    private store: Store
  ) {}

  @Action(GetDealDocumentRequirements)
	getDealDocumentRequirements(
    { getState, patchState }: StateContext<DealDocumentStateModel>,
    { deal, loanProductId }: GetDealDocumentRequirements
  ) {
    const alreadyFetchedDealDocumentRequirements = getState().dealDocumentRequirements
      .findIndex(requirements => requirements.dealId === deal.id) !== -1;

    if (alreadyFetchedDealDocumentRequirements) {
      return;
    }

    patchState({ loading: true });
    return this.dealDocumentsService.getDocumentRequirements(loanProductId).pipe(
      map(documentRequirements => {

        const dealDocumentTypes = this.dealDocumentUtility.docTypes;
        const groupedDealDocuments = _(deal.many_documents).groupBy('category').value();
        const borrowerStateId = this.store.selectSnapshot( BorrowerValueState.valueSelectorByAlias('borrowerState') );
        const hasDisclosureReqPermission = this.store.selectSnapshot( LenderUserState.hasPermission('loanDiscloserRequirement') );
        const disclosureReqState = ['NY','CA'].includes(borrowerStateId?.value || '');

      // if there are documents attached to the deal, attach them to their respective requirement
      if (deal.many_documents) {
        const extras = [this.dealDocumentUtility.signedApplicationRequirement];
        if(hasDisclosureReqPermission && disclosureReqState) {
          extras.push(this.dealDocumentUtility.loanDisclosureRequirement);
        }
        documentRequirements.unshift(...extras);
        documentRequirements.map(requirement => {
          for (const [key, value] of Object.entries(dealDocumentTypes)) {
            if (value.alias === requirement.alias) {
              requirement.category = value;
            }
          }
          if (!requirement.category) {
            requirement.category = {
              display: requirement.name,
              value: requirement.dbName,
              alias: requirement.alias,
            };
          }
          if (requirement.alias === 'application_signed') {
            requirement.requirement = deal.signed_application
              ? [deal.signed_application]
              : [];
          } else if (requirement.alias === 'loanDisclosure') {
            const disclosureDoc = deal.many_documents.filter( doc => doc.category === 'loanDisclosure')[0];
            requirement.requirement = disclosureDoc
              ? [disclosureDoc]
              : [];
          } else {
            requirement.requirement = groupedDealDocuments[requirement.category.value]
              ? groupedDealDocuments[requirement.category.value]
              : [];
            delete groupedDealDocuments[requirement.category.value];
          }
          return requirement;
        });
      }

      // format additional documents into requirement shape for display, if any
      const otherDealDocs: any[] = [];
      if (Object.keys(groupedDealDocuments).length > 0) {
        for (const [key, value] of Object.entries(groupedDealDocuments)) {
          if (!['applicationSigned', 'loanDisclosure'].includes(key) && dealDocumentTypes[key]) {
            otherDealDocs.push({
              name: dealDocumentTypes[key].display,
              requirement: value,
              category: dealDocumentTypes[key],
              requiredForOffer: false,
              requiredForContract: false
            });
          }
        }
      }

      patchState({
        loading: false,
        dealDocumentRequirements: [
          ...getState().dealDocumentRequirements,
          {
            dealId: deal.id,
            documentRequirements: {
              contractRequirements: documentRequirements.filter(req => !req.requiredForOffer && req.requiredForContract),
              offerRequirements: documentRequirements.filter(req => (
                  !req.requiredForContract && req.requiredForOffer
                  || (req.requiredForContract && req.requiredForOffer)
                  || req.alias === 'application_signed'
                )),
              otherDocs: otherDealDocs
            },
            requirementsMet: false
          }
        ]
      });

      this.store.dispatch(new SetDocumentRequirementsMet(deal.id));
    }),
    catchError(error => {
      console.error('Error loading deal document requirements:', error);
      patchState({ loading: false });
      return throwError(() => error);
    }));
  }

  @Action(GetAdvancedDocumentRequirements)
  getAdvancedDocumentRequirements(
    { getState, patchState }: StateContext<DealDocumentStateModel>, 
    { dealId }: GetAdvancedDocumentRequirements
  ) {
    patchState({ dealsLoadingRequirements: { ...getState().dealsLoadingRequirements, [dealId]: true } });
    return this.dealDocumentsService.getAdvancedDocumentRequirements(dealId).pipe(
      catchError((err) => {
        patchState({ dealsLoadingRequirements: { ...getState().dealsLoadingRequirements, [dealId]: false } });
        return throwError(() => err);
      }),
      tap(({ documentRequirements }) => {
        const existingRequirements = getState().advDealDocumentRequirements;
        const existingIndex = existingRequirements.findIndex(req => req.dealId === dealId);
        
        let updatedRequirements;
        if (existingIndex >= 0) {
          // Update existing requirement
          updatedRequirements = [...existingRequirements];
          updatedRequirements[existingIndex] = { dealId, documentRequirements };
        } else {
          // Add new requirement
          updatedRequirements = [...existingRequirements, { dealId, documentRequirements }];
        }
        
        patchState({ 
          advDealDocumentRequirements: updatedRequirements,
          dealsLoadingRequirements: { ...getState().dealsLoadingRequirements, [dealId]: false }
        });
      })
    );
  }

  @Action(GetAdvancedDealDocuments)
  getAdvancedDealDocuments({ getState, patchState }: StateContext<DealDocumentStateModel>, { dealId }: GetAdvancedDealDocuments) {
    // Create a completely new dealsLoadingDocuments object to ensure change detection
    let currentLoadingState = getState().dealsLoadingDocuments;
    let newLoadingState = { ...currentLoadingState };
    newLoadingState[dealId] = true;
    patchState({ dealsLoadingDocuments: newLoadingState });
    return this.dealDocumentsService.getAdvancedDealDocuments(dealId).pipe(
      catchError((err) => {
        // Create a completely new dealsLoadingDocuments object to ensure change detection
        currentLoadingState = getState().dealsLoadingDocuments;
        newLoadingState = { ...currentLoadingState };
        newLoadingState[dealId] = false;
        patchState({ dealsLoadingDocuments: newLoadingState });
        return throwError(() => err);
      }),
      tap((response) => {

        const existingDocuments = getState().advancedDealDocuments;
        const existingIndex = existingDocuments.findIndex(req => req.dealId === dealId);
        
        let updatedDocuments;
        if (existingIndex >= 0) {
          // Update existing documents
          updatedDocuments = [...existingDocuments];
          updatedDocuments[existingIndex] = { dealId, documents: response };
        } else {
          // Add new documents
          updatedDocuments = [...existingDocuments, { dealId, documents: response }];
        }
        
        // Create a completely new dealsLoadingDocuments object to ensure change detection
        currentLoadingState = getState().dealsLoadingDocuments;
        newLoadingState = { ...currentLoadingState };
        newLoadingState[dealId] = false;
        
        patchState({ 
          advancedDealDocuments: updatedDocuments,
          dealsLoadingDocuments: newLoadingState
        });
      })
    );
  }

  @Action(UploadDealDocument)
  uploadDealDocument(
    { getState, patchState }: StateContext<DealDocumentStateModel>,
    { file, borrowerId, dealId, category, requirementGroup }: UploadDealDocument
  ) {
    patchState({ uploading: true });
    return this.dealDocumentsService.uploadDealDocument(file, borrowerId, dealId, category).pipe(catchError(err => {
      const newAlert: Alert = {
        level: 'error',
        message: 'There was an error uploading the deal document. Please try again.',
        dismissDelay: 5000
      };
      this.store.dispatch(new CreateNewDealAlert(dealId, newAlert));
      patchState({ uploading: false });
      return of();
    }), map(docUpload => {
      // if uploading a signed application, make additional call to update deal with document id
      if (category === 'applicationSigned') {
        this.dealDocumentsService.attachSignedApplication(dealId, docUpload.id).pipe(take(1)).subscribe();
      }
      const dealRequirementsStateCopy = [ ...getState().dealDocumentRequirements ];
      const dealRequirementsIndex = getState().dealDocumentRequirements.findIndex(ddr => ddr.dealId === dealId);
      const dealRequirementsCopy = { ...getState().dealDocumentRequirements[dealRequirementsIndex] };
      dealRequirementsCopy.documentRequirements[requirementGroup].forEach((req: DealApplicationRequirement) => {
        if (req.category.value === category) {
          req.requirement.push(docUpload);
        }
      });
      dealRequirementsStateCopy[dealRequirementsIndex] = dealRequirementsCopy;
      patchState({
        dealDocumentRequirements: dealRequirementsStateCopy,
        uploading: false
      });
      this.store.dispatch(new SetDocumentRequirementsMet(dealId));
    }));
  }

  @Action(AdvancedDealDocumentPost)
  uploadAdvancedDealDocument(
    { getState, patchState }: StateContext<DealDocumentStateModel>,
    { file, formData }: AdvancedDealDocumentPost
  ) {
    patchState({ uploading: true });
    return this.dealDocumentsService.postAdvancedDealDocument(file, formData).pipe(
      map(uploadedDocument => {
        const dealId = formData.get('dealId');
        if (dealId) {
          const dealIdNum = typeof dealId === 'string' ? parseInt(dealId) : dealId;
          const existingDocuments = getState().advancedDealDocuments;
          const existingIndex = existingDocuments.findIndex(doc => doc.dealId === dealIdNum);
          
          let updatedDocuments;
          if (existingIndex >= 0) {
            // Update existing deal documents
            updatedDocuments = [...existingDocuments];
            updatedDocuments[existingIndex] = {
              ...updatedDocuments[existingIndex],
              documents: [...updatedDocuments[existingIndex].documents, uploadedDocument]
            };
          } else {
            // Add new deal documents entry
            updatedDocuments = [...existingDocuments, {
              dealId: dealIdNum,
              documents: [uploadedDocument]
            }];
          }
          
          patchState({ 
            advancedDealDocuments: updatedDocuments,
            uploading: false 
          });

          this.store.dispatch(new GetAdvancedDocumentRequirements(dealIdNum as number));
          this.store.dispatch(new GetAdvancedDealDocuments(dealIdNum as number));
        }
        return uploadedDocument;
      }),
      catchError(err => {
        const newAlert: Alert = {
          level: 'error',
          message: 'There was an error uploading the deal document. Please try again.',
          dismissDelay: 5000
        };
        const dealId = formData.get('dealId');

        if (dealId && typeof dealId === 'number') {
          this.store.dispatch(new CreateNewDealAlert(dealId, newAlert));
        }

        patchState({ uploading: false });
        return of();
      })
    );
  }

  @Action(ReviewDealDocument)
  reviewDealDocument(
    { getState, patchState }: StateContext<DealDocumentStateModel>,
    { dealId, documentId }: ReviewDealDocument
  ) {
    return this.dealDocumentsService.reviewDealDocument(dealId, documentId).pipe(
      map(updatedDocument => {
        // Update the document in advancedDealDocuments
        const existingDocuments = getState().advancedDealDocuments;
        const dealIndex = existingDocuments.findIndex(doc => doc.dealId === dealId);
        
        if (dealIndex >= 0) {
          const updatedDocuments = [...existingDocuments];
          const documentIndex = updatedDocuments[dealIndex].documents.findIndex(doc => doc.id === documentId);
          
          if (documentIndex >= 0) {
            updatedDocuments[dealIndex] = {
              ...updatedDocuments[dealIndex],
              documents: [
                 ...updatedDocuments[dealIndex].documents.slice(0, documentIndex),
                 updatedDocument as unknown as Document,
                 ...updatedDocuments[dealIndex].documents.slice(documentIndex + 1)
               ]
            };
            
            patchState({ advancedDealDocuments: updatedDocuments });
          }
        }
        
        // Dispatch GetDealDocumentRequirements action
        this.store.dispatch(new GetAdvancedDocumentRequirements(dealId));
        this.store.dispatch(new GetAdvancedDealDocuments(dealId));
        
        return updatedDocument;
      }),
      catchError(err => {
        const newAlert: Alert = {
          level: 'error',
          message: 'There was an error accepting this document. Please try again.',
          dismissDelay: 5000
        };
        this.store.dispatch(new CreateNewDealAlert(dealId, newAlert));
        return of();
      })
    );
  }

  @Action(RejectDealDocument) 
  rejectDealDocument(
    { getState, patchState }: StateContext<DealDocumentStateModel>,
    { dealId, documentId, rejectionReason }: RejectDealDocument
  ) {
    return this.dealDocumentsService.rejectDealDocument(dealId, documentId, rejectionReason).pipe(
      map(updatedDocument => {
        // Update the document in advancedDealDocuments
        const existingDocuments = getState().advancedDealDocuments;
        const dealIndex = existingDocuments.findIndex(doc => doc.dealId === dealId);
        
        if (dealIndex >= 0) {
          const updatedDocuments = [...existingDocuments];
          const documentIndex = updatedDocuments[dealIndex].documents.findIndex(doc => doc.id === documentId);
          
          if (documentIndex >= 0) {
            updatedDocuments[dealIndex] = {
              ...updatedDocuments[dealIndex],
              documents: [
                ...updatedDocuments[dealIndex].documents.slice(0, documentIndex),
                updatedDocument as unknown as Document,
                ...updatedDocuments[dealIndex].documents.slice(documentIndex + 1)
              ]
            };
            
            patchState({ advancedDealDocuments: updatedDocuments });
          }
        }
        
        // Dispatch GetDealDocumentRequirements action
        this.store.dispatch(new GetAdvancedDocumentRequirements(dealId));
        
        return updatedDocument;
      }),
      catchError(err => {
        const newAlert: Alert = {
          level: 'error',
          message: 'There was an error rejecting this document. Please try again.',
          dismissDelay: 5000
        };
        this.store.dispatch(new CreateNewDealAlert(dealId, newAlert));
        return of();
      })
    );
  }

  @Action(ClearDealDocumentStatus)
  clearDealDocumentStatus(
    { getState, patchState }: StateContext<DealDocumentStateModel>,
    { dealId, documentId }: ClearDealDocumentStatus
  ) {
    return this.dealDocumentsService.clearDealDocumentStatus(dealId, documentId).pipe(
      map(updatedDocument => {
        // Update the document in advancedDealDocuments
        const existingDocuments = getState().advancedDealDocuments;
        const dealIndex = existingDocuments.findIndex(doc => doc.dealId === dealId);
        
        if (dealIndex >= 0) {
          const updatedDocuments = [...existingDocuments];
          const documentIndex = updatedDocuments[dealIndex].documents.findIndex(doc => doc.id === documentId);
          
          if (documentIndex >= 0) {
            updatedDocuments[dealIndex] = {
              ...updatedDocuments[dealIndex],
              documents: [
                ...updatedDocuments[dealIndex].documents.slice(0, documentIndex),
                updatedDocument as unknown as Document,
                ...updatedDocuments[dealIndex].documents.slice(documentIndex + 1)
              ]
            };
            
            patchState({ advancedDealDocuments: updatedDocuments });
          }
        }
        
        // Dispatch GetDealDocumentRequirements action
        this.store.dispatch(new GetAdvancedDocumentRequirements(dealId));
        this.store.dispatch(new GetAdvancedDealDocuments(dealId));
        
        return updatedDocument;
      }),
      catchError(err => {
        const newAlert: Alert = {
          level: 'error',
          message: 'There was an error clearing the document status. Please try again.',
          dismissDelay: 5000
        };
        this.store.dispatch(new CreateNewDealAlert(dealId, newAlert));
        return of();
      })
    );
  }

  @Action(PostDocumentRequirements)
  postDocumentRequirements(
    { getState, patchState }: StateContext<DealDocumentStateModel>,
    { dealId, documentRequirements }: PostDocumentRequirements
  ) {
    return this.dealDocumentsService.postDocumentRequirement(dealId, documentRequirements).pipe(
      map(response => {
        // Dispatch GetAdvancedDocumentRequirements action after successful post
        this.store.dispatch(new GetAdvancedDocumentRequirements(dealId));
        return response;
      }),
      catchError(err => {
        const newAlert: Alert = {
          level: 'error',
          message: 'There was an error posting document requirements. Please try again.',
          dismissDelay: 5000
        };
        this.store.dispatch(new CreateNewDealAlert(dealId, newAlert));
        return of();
      })
    );
  }

  @Action(DeleteDocumentRequest)
  deleteDocumentRequest(
    { getState, patchState }: StateContext<DealDocumentStateModel>,
    { dealId, documentRequirement }: DeleteDocumentRequest
  ) {
    return this.dealDocumentsService.deleteDocumentRequirement(dealId, documentRequirement).pipe(
      map(response => {
        // Dispatch GetAdvancedDocumentRequirements action after successful delete
        this.store.dispatch(new GetAdvancedDocumentRequirements(dealId));
        return response;
      }),
      catchError(err => {
        const newAlert: Alert = {
          level: 'error',
          message: 'There was an error deleting the document requirement. Please try again.',
          dismissDelay: 5000
        };
        this.store.dispatch(new CreateNewDealAlert(dealId, newAlert));
        return of();
      })
    );
  }

  @Action(PatchDealDocument)
  patchDealDocument(
    { getState, patchState }: StateContext<DealDocumentStateModel>,
    { dealId, document }: PatchDealDocument
  ) {
    return this.dealDocumentsService.patchDealDocument(dealId, document).pipe(
      map(updatedDocument => {
        // Update the document in advancedDealDocuments
        const existingDocuments = getState().advancedDealDocuments;
        const dealIndex = existingDocuments.findIndex(doc => doc.dealId === dealId);
        
        if (dealIndex >= 0) {
          const updatedDocuments = [...existingDocuments];
          const documentIndex = updatedDocuments[dealIndex].documents.findIndex(doc => doc.id === document.id);
          
          if (documentIndex >= 0) {
            updatedDocuments[dealIndex] = {
              ...updatedDocuments[dealIndex],
              documents: [
                ...updatedDocuments[dealIndex].documents.slice(0, documentIndex),
                updatedDocument as unknown as Document,
                ...updatedDocuments[dealIndex].documents.slice(documentIndex + 1)
              ]
            };
            
            patchState({ advancedDealDocuments: updatedDocuments });
          }
        }
        
        // Dispatch GetDealDocumentRequirements action
        this.store.dispatch(new GetAdvancedDocumentRequirements(dealId));
        this.store.dispatch(new GetAdvancedDealDocuments(dealId));
        return updatedDocument;
      }),
      catchError(err => {
        const newAlert: Alert = {
          level: 'error',
          message: 'There was an error updating the document. Please try again.',
          dismissDelay: 5000
        };
        this.store.dispatch(new CreateNewDealAlert(dealId, newAlert));
        return of();
      })
    );
  }

  @Action(AttachDealDocument)
  attachDealDocument(
    { getState, patchState }: StateContext<DealDocumentStateModel>,
    { dealId, document, requirementGroup }: AttachDealDocument
  ) {
    patchState({ uploading: true });
    return this.documentsService.update({ id: document.id, dealId, attach: true, category: document.category }).pipe(catchError(err => {
      const newAlert: Alert = {
        level: 'error',
        message: 'There was an error attaching this document to the deal. Please try again.',
        dismissDelay: 5000
      };
      this.store.dispatch(new CreateNewDealAlert(dealId, newAlert));
      patchState({ uploading: false });
      return of();
    }), map(updatedDoc => {
      const dealRequirementsStateCopy = [ ...getState().dealDocumentRequirements ];
      const dealRequirementsIndex = getState().dealDocumentRequirements.findIndex(ddr => ddr.dealId === dealId);
      const dealRequirementsCopy = { ...getState().dealDocumentRequirements[dealRequirementsIndex] };
      dealRequirementsCopy.documentRequirements[requirementGroup].forEach((req: DealApplicationRequirement) => {
        if (req.category.value === document.category) {
          req.requirement.push(document);
        }
      });
      dealRequirementsStateCopy[dealRequirementsIndex] = dealRequirementsCopy;
      patchState({ dealDocumentRequirements: dealRequirementsStateCopy, uploading: false });
      // if attaching a signed application, make additional call to update deal with document id
      if (updatedDoc.category === 'applicationSigned') {
        this.dealDocumentsService.attachSignedApplication(dealId, updatedDoc.id).pipe(take(1)).subscribe();
      }
      this.store.dispatch(new SetDocumentRequirementsMet(dealId));
    }));
  }

  @Action(AttachAdvancedDealDocument)
  attachAdvancedDealDocument(
    { getState, patchState }: StateContext<DealDocumentStateModel>,
    { dealId, document }: AttachAdvancedDealDocument
  ) {
    patchState({ uploading: true });
    return this.dealDocumentsService.attachAdvancedDealDocument(dealId, document.id).pipe(catchError(err => {
      const newAlert: Alert = {
        level: 'error',
        message: 'There was an error attaching this document to the deal. Please try again.',
        dismissDelay: 5000
      };
      this.store.dispatch(new CreateNewDealAlert(dealId, newAlert));
      patchState({ uploading: false });
      return of();
    }), map(updatedDoc => {
      const existingDocuments = getState().advancedDealDocuments;
      const existingIndex = existingDocuments.findIndex(doc => doc.dealId === dealId);
          
      let updatedDocuments;
      if (existingIndex >= 0) {
        // Update existing deal documents
        updatedDocuments = [...existingDocuments];
        updatedDocuments[existingIndex] = {
          ...updatedDocuments[existingIndex],
          documents: [...updatedDocuments[existingIndex].documents, updatedDoc]
        };
      } else {
        // Add new deal documents entry
        updatedDocuments = [...existingDocuments, {
          dealId: dealId,
          documents: [updatedDoc]
        }];
      }
          
      patchState({ 
        advancedDealDocuments: updatedDocuments,
        uploading: false 
      });
      this.store.dispatch(new GetAdvancedDocumentRequirements(dealId));
      
      if (updatedDoc.category === 'applicationSigned') {
        this.dealDocumentsService.attachSignedApplication(dealId, updatedDoc.id).pipe(take(1)).subscribe();
      }
    }));
  }

  @Action(DetachDealDocument)
  detachDealDocument(
    { getState, patchState }: StateContext<DealDocumentStateModel>,
    { dealId, document, requirementGroup }: DetachDealDocument
  ) {
    const dealRequirementsStateCopy = [ ...getState().dealDocumentRequirements ];
    const dealRequirementsIndex = getState().dealDocumentRequirements.findIndex(ddr => ddr.dealId === dealId);
    const dealRequirementsCopy = { ...getState().dealDocumentRequirements[dealRequirementsIndex] };
    dealRequirementsCopy.documentRequirements[requirementGroup].forEach((req: DealApplicationRequirement) => {
      if (req.category.value === document.category) {
        req.requirement = req.requirement.filter(doc => doc.id !== document.id);
      }
    });
    dealRequirementsStateCopy[dealRequirementsIndex] = dealRequirementsCopy;
    patchState({ dealDocumentRequirements: dealRequirementsStateCopy });
    return this.documentsService.update({ id: document.id, dealId, detach: true }).pipe(catchError(err => {
      dealRequirementsCopy.documentRequirements[requirementGroup].forEach((req: DealApplicationRequirement) => {
        if (req.category.value === document.category) {
          req.requirement.push(document);
        }
      });
      dealRequirementsStateCopy[dealRequirementsIndex] = dealRequirementsCopy;
      patchState({ dealDocumentRequirements: dealRequirementsStateCopy });
      const newAlert: Alert = {
        level: 'error',
        message: 'There was an error detaching this document from the deal. Please try again.',
        dismissDelay: 5000
      };
      this.store.dispatch(new CreateNewDealAlert(dealId, newAlert));
      return of();
    }), map(updatedDoc => {
      // if detaching a signed application, make additional call to update deal
      if (updatedDoc.category === 'applicationSigned') {
        this.dealDocumentsService.detachSignedApplication(dealId).pipe(take(1)).subscribe();
      }
      this.store.dispatch(new SetDocumentRequirementsMet(dealId));
    }));
  }

  @Action(AddDocumentCategory)
  addDocumentCategory(
    { getState, patchState }: StateContext<DealDocumentStateModel>,
    { dealId, documentCategory }: AddDocumentCategory
  ) {
    const dealDocumentTypes = this.dealDocumentUtility.docTypes;
    if (dealDocumentTypes[documentCategory.id]) {
      const newCategory = {
        name: dealDocumentTypes[documentCategory.id].display,
        requirement: [],
        category: dealDocumentTypes[documentCategory.id],
        requiredForOffer: false,
        requiredForContract: false
      };
      const dealRequirementsStateCopy = [ ...getState().dealDocumentRequirements ];
      const dealRequirementsIndex = getState().dealDocumentRequirements?.findIndex(ddr => ddr.dealId === dealId);
      const dealRequirementsCopy = { ...getState().dealDocumentRequirements[dealRequirementsIndex] };
      dealRequirementsCopy?.documentRequirements?.otherDocs?.push(newCategory);
      dealRequirementsStateCopy[dealRequirementsIndex] = dealRequirementsCopy;
      patchState({ dealDocumentRequirements: dealRequirementsStateCopy });
    }
  }


  @Action(SetDocumentRequirementsMet)
  setDocumentRequirementsMet({ getState, patchState }: StateContext<DealDocumentStateModel>, { dealId }: SetDocumentRequirementsMet) {
    const dealRequirementsStateCopy = [ ...getState().dealDocumentRequirements ];
    const dealDocumentRequirementsIndex = dealRequirementsStateCopy.findIndex(reqs => reqs.dealId === dealId);
    const dealDocumentRequirements = { ...dealRequirementsStateCopy[dealDocumentRequirementsIndex] };
    const offerRequirements = dealDocumentRequirements?.documentRequirements?.offerRequirements;
    const reqDocsCheck: boolean[] = offerRequirements
      ? offerRequirements.map(req => req.requirement && req.requirement.length && req.requirement.length > 0 ? true : false)
      : [];
    if (reqDocsCheck.includes(false)) {
      dealDocumentRequirements.requirementsMet = false;
    } else {
      dealDocumentRequirements.requirementsMet = true;
    }
    dealRequirementsStateCopy[dealDocumentRequirementsIndex] = dealDocumentRequirements;
    patchState({ dealDocumentRequirements: dealRequirementsStateCopy });
  }

  @Action(ClearDealDocumentRequirements)
  clearDealDocumentRequirements({ patchState }: StateContext<DealDocumentStateModel>, {}: ClearDealDocumentRequirements) {
    patchState({ dealDocumentRequirements: [] });
  }
}
